import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactTooltip from 'react-tooltip';
import H1 from 'components/H1';
import { Flex, Box, Button } from 'rebass';
import { Link } from 'react-router-dom';
import { Label, Select, Input, Checkbox, Radio } from '@rebass/forms';
import helpers from 'utils/helpers';
import helpersScreener from 'utils/helpers-screener';
import moment from 'moment-timezone';
import NiceDropdown from 'components/NiceDropdown';
import { convertFromRaw, convertToRaw, Editor, EditorState } from 'draft-js';
import DatePicker from 'react-datepicker';
import analytics from 'utils/analytics';
import { GeoIndicator } from 'components/GeoIndicator';
import {
    FiSidebar,
    FiCheckCircle,
    FiXCircle,
    FiAlertCircle,
    FiCheckSquare,
    FiMinus,
    FiMoreVertical,
    FiFileText,
    FiFile,
    FiEye,
    FiUser,
    FiCalendar
} from 'react-icons/fi';
import {
    STUDY_TABLE_COLUMN_ASSIGNED,
    STUDY_TABLE_COLUMN_SPOT,
    STUDY_TABLE_COLUMN_CONFIRMED,
    STUDY_TABLE_COLUMN_LOCATION,
    STUDY_TABLE_COLUMN_PAID,
    STUDY_TABLE_COLUMN_LAST_CONTACTED,
    SP_STATUS_KEY_SCHEDULED,
    SP_STATUS_KEY_COMPLETED,
    SP_STATUS_KEY_PAID_SENT,
    SP_STATUS_KEY_HOLD,
    SP_STATUS_KEY_PAYMENT_FAILED,
    COL_FIRST_NAME,
    COL_LAST_NAME,
    IS_QUALIFIED,
    STUDY_TABLE_COLUMN_NOTES,
    STUDY_PERSON_INCENTIVE_STATUS,
    INCENTIVE_BADGE_CONFIG,
    DATE_DISPLAY_FORMAT,
    TABLE_CHECKED_STATUS,
    TABLE_CHECKED_STATUS_LABEL
} from 'utils/constants';
import ReactModal from 'react-modal';
import ReactModalActions from 'components/ReactModalActions';

import helpersStudy from 'utils/helpers-study';
import LoadingIndicator from 'components/LoadingIndicator';
import LoadingWrapper from 'components/LoadingIndicator/LoadingWrapper';
import StatusLabel from 'components/StatusLabel';
import classNames from 'classnames';
import { Tr, TableV2 } from 'components/Table';
import Portal from 'components/Portal';
import { PositionerV2 } from 'components/Positioner';
import { useToastHelper } from 'hooks/useToastHelper';
import useMutation from 'hooks/useMutation';
import useQuery from 'hooks/useQuery';
import service_studies from 'services/studies';
import { Skeleton } from 'components/Skeleton';
import sessionEditorStyles from 'styles/session-editor.module.css';
import { SessionDatepickerModal } from 'components/SessionDatepickerModal';
import { displayHumanDate } from 'utils/displayHumanDate';
import { formatDate } from 'utils/formatDate';
import { renderDate } from 'components/ProtectedField';
import PaymentModal from 'components/PaymentModal';
import { checkIfPaymentEditable } from 'utils/checkIfPaymentEditable';
import { getIncentiveDisplayState } from 'utils/getIncentiveDisplayState';
import { Badge } from 'components/Badge';
import ManualPaymentModal from 'components/ManualPaymentModal';
import NiceModal from 'components/NiceModal';
import SessionRecordingsDropdown from 'components/Participant/SessionRecordingsDropdown';
import { produce } from 'immer';
import { ProtectedField, ProtectedFieldComposite, useProtectedCompactRender } from 'components/ProtectedField';
import { PANEL_PROPERTY_TYPE } from 'components/Study/ManageColumns';

const COL_WIDTH = 140;
const COL_MIN_WIDTH = 100;
const avatar_style = { width: '16px', height: '16px', margin: '2px 4px 0 0' };

/**
 * Status label for participant status
 *
 * @param {Object} param0
 * @param {Object} param0.studyPerson study person object
 */
function ParticipantStatusLabel({ studyPerson }) {
    const status = useMemo(() => helpersStudy.getHumanReadableSPStatus(studyPerson.status), [studyPerson.status]);
    const color = useMemo(() => {
        if ([SP_STATUS_KEY_SCHEDULED, SP_STATUS_KEY_COMPLETED, SP_STATUS_KEY_PAID_SENT].includes(studyPerson.status)) {
            return 'green';
        } else if ([SP_STATUS_KEY_HOLD].includes(studyPerson.status)) {
            return 'orange';
        } else if ([SP_STATUS_KEY_PAYMENT_FAILED].includes(studyPerson.status)) {
            return 'danger';
        } else {
            return 'gray';
        }
    }, [studyPerson.status]);

    if (!status) return null;

    return <StatusLabel className="fs-body-12" color={color} status={status} />;
}

function PersonLink({ name, onClick }) {
    return (
        <span onClick={onClick} className="link muted va-top">
            {name}
        </span>
    );
}

function ScreenersDropdown({
    setShowParticipantMoreDropdownSpId,
    showParticipantMoreDropdownSpId,
    items,
    studyPerson
}) {
    const anchorRef = useRef(null);

    return (
        <>
            <Button
                ref={anchorRef}
                variant="secondary"
                className="secondary-tiny secondary-tiny-icon float-right study-person-more-hover"
                style={showParticipantMoreDropdownSpId === studyPerson.id ? { visibility: 'visible' } : {}}
                onClick={() => setShowParticipantMoreDropdownSpId(studyPerson.id)}
            >
                <FiMoreVertical />
            </Button>
            <Portal isOpen={showParticipantMoreDropdownSpId === studyPerson.id}>
                <PositionerV2 anchorRef={anchorRef}>
                    <NiceDropdown
                        width="160px"
                        style={{ inset: 'auto', position: 'static' }}
                        onClose={() => {
                            setShowParticipantMoreDropdownSpId(null);
                        }}
                        items={items}
                    />
                </PositionerV2>
            </Portal>
        </>
    );
}

/**
 * Component that processes and displays plain text content from serialized notes.
 *
 * @param {Object} props The properties object.
 * @param {string|null} props.[notes] A JSON string representing serialized notes content.
 * @return {React.ReactNode} A React element that displays the plain text content of the notes.
 */
function NotesCell({ notes }) {
    const content = useMemo(() => {
        if (!notes) return '';

        try {
            return convertFromRaw(JSON.parse(notes)).getPlainText();
        } catch (error) {
            return '';
        }
    }, [notes]);

    return <>{content}</>;
}

/**
 * A modal component for editing notes, allowing users to input, update, and save text in a rich text editor.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.value - The initial value of the notes, provided as a JSON string that represents the editor's content.
 * @param {(value: string) => void} props.onChange - Callback function triggered when the editor content changes. Receives the updated content as a JSON string.
 * @param {() => void} props.onClose - Callback function triggered when the modal is closed.
 * @param {() => void} props.onSave - Callback function triggered when the "Save" button is clicked.
 * @param {boolean} props.isOpen - Determines whether the modal is open or closed.
 *
 * @return {React.ReactNode} A rendered modal component containing the rich text editor and action buttons.
 */
function NotesEditorModal({ value, onChange, onClose, onSave, isOpen }) {
    const [editorState, setEditorState] = useState(
        /**
         * @returns {EditorState}
         */
        () => {
            try {
                return EditorState.createWithContent(convertFromRaw(JSON.parse(value)));
            } catch (error) {
                return EditorState.createEmpty();
            }
        }
    );

    const updateNotes = editorState => {
        onChange(JSON.stringify(convertToRaw(editorState.getCurrentContent())));
        setEditorState(editorState);
    };

    return (
        <Portal isOpen={isOpen}>
            <NiceModal
                isOpen={isOpen}
                shouldCloseOnOverlayClick
                onRequestClose={onClose}
                style={{ content: { width: '620px' } }}
                title="Add Note"
            >
                <div
                    style={{ height: '340px' }}
                    className="overflow-y-auto border-1 rounded border-iron border-solid p-2"
                >
                    <Editor
                        editorState={editorState}
                        onChange={updateNotes}
                        placeholder="Start typing notes for this participant..."
                    />
                </div>
                <div className="modal-actions">
                    <Button type="button" variant="secondary-gray" mr={3} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="button" variant="primary" onClick={onSave}>
                        Save
                    </Button>
                </div>
            </NiceModal>
        </Portal>
    );
}

/**
 * AssignedUserEditor component allows selection of a user from a list of collaborators.
 * The component includes a search functionality to filter collaborators, displays a list of matching collaborators,
 * and allows selection via radio buttons.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Array<{
 *     user_id: number,
 *     user: {
 *         name: string
 *     }
 * }>} props.[collaborators] - An array of collaborator objects containing user details.
 * @param {boolean} props.isCollaboratorsLoading - A boolean indicating whether collaborators data is still loading.
 * @param {number|string} props.value - The currently selected collaborator's user ID.
 * @param {(value: number) => void} props.onChange - A callback function triggered when the selected collaborator changes.
 * @return {React.ReactNode} A JSX element representing the AssignedUserEditor component.
 */
function AssignedUserEditor({ collaborators, isCollaboratorsLoading, value, onChange }) {
    const [search, setSearch] = useState('');
    const options = useMemo(() => {
        const regex = new RegExp(search, 'i');
        const options = [{ value: null, label: 'Un-assign' }];

        if (collaborators) {
            options.push(
                ...collaborators.map(collaborator => ({
                    value: collaborator.user_id,
                    label: collaborator.user.name
                }))
            );
        }

        return options.filter(option => regex.test(option.label));
    }, [collaborators, search]);

    return (
        <>
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search collaborators" />
            <hr className="my-3 -mx-2" />
            {!isCollaboratorsLoading && options.length > 0 && (
                <fieldset className="border-0 p-0 m-0">
                    <legend className="sr-only">Select a collaborator</legend>
                    {options.map(option => (
                        <Label key={option.value} className="!mb-0 h-8">
                            <Radio
                                name="collaborators-radio"
                                id={`collaborator-${option.value}`}
                                value={option.value}
                                checked={value === option.value}
                                onChange={() => onChange(option.value)}
                                className="!mr-1 !my-1.5"
                            />
                            <span className="leading-8">{option.label}</span>
                        </Label>
                    ))}
                </fieldset>
            )}
            {isCollaboratorsLoading && <Skeleton height="32px" />}
            {!isCollaboratorsLoading && options.length === 0 && (
                <p className="text-center m-0 leading-8">No collaborators found</p>
            )}
        </>
    );
}

/**
 * Component that renders a radio button editor with search functionality,
 * allowing users to filter and select from a set of options.
 *
 * @param {Object} props - The properties object passed to the component.
 * @param {string|number} props.value - The currently selected value for the radio editor.
 * @param {(value: string | number) => void} props.onChange - Callback function triggered when the selected value changes.
 * @param {Array<{
 *     value: number | string
 *     label: string
 * }>} props.options - An array of options to display.
 * @return {React.ReactNode}
 */
function RadioEditor({ value, onChange, options }) {
    const [search, setSearch] = useState('');
    const filteredOptions = useMemo(() => {
        const regex = new RegExp(search, 'i');

        return options.filter(option => regex.test(option.label));
    }, [options, search]);

    return (
        <>
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search" />
            <hr className="my-3 -mx-2" />
            {filteredOptions.length > 0 && (
                <fieldset className="border-0 p-0 m-0">
                    <legend className="sr-only">Select an option</legend>
                    {filteredOptions.map(option => (
                        <Label key={option.value} className="!mb-0 h-8">
                            <Radio
                                name="radio-editor"
                                id={`radio-editor-option-${option.value}`}
                                value={option.value}
                                checked={value === option.value}
                                onChange={() => onChange(option.value)}
                                className="!mr-1 !my-1.5"
                            />
                            <span className="leading-8">{option.label}</span>
                        </Label>
                    ))}
                </fieldset>
            )}
            {filteredOptions.length === 0 && <p className="text-center m-0 leading-8">No options found</p>}
        </>
    );
}

/**
 * A component that allows editing of session details, providing options for selecting available spots
 * or setting a custom session date.
 *
 * @param {Object} params - The parameters for the SessionEditor component.
 * @param {{ id: number, timezone?: string }} params.study - The study data containing details like the study timezone and ID.
 * @param {string} params.[value] - The current selected spot or session date in string format.
 * @param {(value?: string) => void} params.onChange - Callback function triggered when the selection or date changes.
 * @param {(value: boolean) => void} params.setLocked - Function to lock or unlock the editor when opening or closing the custom date picker.
 *
 * @return {JSX.Element} A React component for session editing, including available spots and date picker functionality.
 */
function SessionEditor({ study, value, onChange, setLocked }) {
    const { addErrorToast } = useToastHelper();
    const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
    const { data, isLoading } = useQuery({
        queryFn: ({ studyId, showTakenSpots }) => service_studies.getStudyAvailability(studyId, showTakenSpots),
        variables: {
            studyId: study.id,
            showTakenSpots: false
        },
        onError: (_, errorText) => {
            addErrorToast(errorText);
        }
    });
    const options = useMemo(() => {
        if (!data) return [];

        const raw = helpersStudy.generateStudyAvailabilityArray(data, study.timezone, study.timezone);

        // Values from the API are in the study timezone, so we need to convert them to UTC
        return raw.map(item => {
            if (item.id) {
                item.id = moment
                    .tz(item.id, study.timezone)
                    .utc()
                    .toISOString();
            }

            return item;
        });
    }, [data, study]);

    return (
        <>
            <Input value={helpersStudy.displayStudyPersonSpot({ spot: value }, study)} disabled />
            <hr className="my-3 -mx-2" />
            <fieldset className="border-0 p-0 m-0 overflow-y-auto" style={{ maxHeight: '320px' }}>
                <legend className="sr-only">Select a spot</legend>
                {options.map(option => {
                    const isLabel = option.label;

                    if (isLabel)
                        return (
                            <p className={sessionEditorStyles.dayLabel} key={option.title}>
                                {option.title}
                            </p>
                        );

                    return (
                        <Label key={option.id} className="!mb-0 px-4 h-8 !leading-8 rounded hover:bg-alabaster">
                            {option.title}
                            <input
                                type="radio"
                                name="collaborators-radio"
                                id={`spot-${option.id}`}
                                value={option.id}
                                checked={value === option.id}
                                onChange={e => onChange(e.target.value)}
                                className="sr-only"
                            />
                        </Label>
                    );
                })}
                {isLoading && <Skeleton height="400px" />}
                {!isLoading && options.length === 0 && <p className="text-center m-0 leading-8">No spots available</p>}
            </fieldset>
            <hr className="my-3 -mx-2" />
            <Button
                variant="secondary"
                onClick={() => {
                    setLocked(true);
                    setIsDatepickerOpen(true);
                }}
                className="!m-0 !w-full"
            >
                <FiCalendar /> Set Custom
            </Button>
            <SessionDatepickerModal
                isOpen={isDatepickerOpen}
                onClose={() => {
                    setLocked(false);
                    setIsDatepickerOpen(false);
                }}
                timezone={study.timezone}
                onSubmit={date =>
                    onChange(
                        moment
                            .tz(date, study.timezone)
                            .utc()
                            .format()
                    )
                }
                defaultValue={moment.utc(value || undefined).toDate()}
            />
        </>
    );
}

/**
 * Provides a date editing component that handles date input based on a timezone or UTC format.
 * Displays the current date value and allows users to select a new date, which is then passed back
 * to the provided `onChange` function in the appropriate format.
 *
 * @param {Object} props The properties passed to the DateEditor component.
 * @param {string|null} props.value The current date value in ISO string format or null.
 * @param {(value: string) => void} props.onChange Callback function triggered when the date is changed.
 *        Receives the updated date as a parameter (either in UTC or based on the specified timezone).
 * @param {string} [props.timezone] Optional timezone to handle date conversions. Defaults to UTC if not specified.
 * @return {React.ReactNode} A React element that renders a date editor with timezone-aware functionality.
 */
function DateEditor({ value, onChange, timezone }) {
    const selected =
        value &&
        (timezone
            ? moment
                  .utc(value)
                  .tz(timezone)
                  .toDate()
            : moment.utc(value).toDate());

    const changeDate = date => {
        // If a timezone is specified, ensure the date is interpreted in that timezone
        if (timezone) {
            // Create a moment in the specified timezone, then convert to UTC for storage
            const dateInTimezone = moment.tz(date, timezone);
            onChange(dateInTimezone.utc().format());
        } else {
            onChange(date.toISOString());
        }
    };

    return (
        <>
            <Input value={displayHumanDate(value, timezone)} disabled />
            <hr className="my-3 -mx-2" />
            <div className="text-center">
                <DatePicker
                    inline
                    className="theme-input"
                    style={{ width: '100%' }}
                    showTimeInput
                    onChange={changeDate}
                    selected={selected}
                />
            </div>
        </>
    );
}

/**
 * A functional component for a text editor with a text input field that supports saving on pressing the Enter key.
 *
 * @param {Object} props The properties object.
 * @param {string|null} props.value The current value of the input field.
 * @param {function(string): void} props.onChange Callback function triggered when the input value changes.
 * @param {string} props.[placeholder] The placeholder text displayed in the input field when it's empty.
 * @param {function(): void} [props.onSave] Optional callback function triggered when the Enter key is pressed.
 *
 * @return {React.ReactNode} A JSX element containing the Input field for the text editor.
 */
function TextEditor({ value, onChange, placeholder, onSave }) {
    // The input component doesn't like null value and prefers empty string instead
    const parsedValue = value === null ? '' : value;

    const handleEnter = event => {
        if (event.key === 'Enter' && !event.repeat) {
            onSave && onSave();
        }
    };

    return (
        <>
            <Input
                value={parsedValue}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                autoFocus
                onKeyDown={handleEnter}
            />
        </>
    );
}

/**
 * A React component for rendering a searchable checkbox editor to select multiple options.
 *
 * @param {Object} props The properties passed to the component.
 * @param {Array|null} props.value The list of currently selected option values.
 * @param {(value: Array) => void} props.onChange Callback function triggered when the selection changes.
 * @param {Array<{
 *     value: string
 *     label: string
 * }>} props.options An array of options,
 * @return {React.ReactNode}
 */
function CheckboxEditor({ value, onChange, options }) {
    const [search, setSearch] = useState('');
    const filteredOptions = useMemo(() => {
        const regex = new RegExp(search, 'i');
        return options.filter(option => regex.test(option.label));
    }, [options, search]);
    const selectedValues = value ? value : [];

    const handleCheckboxChange = optionValue => {
        if (selectedValues.includes(optionValue)) {
            onChange(selectedValues.filter(v => v !== optionValue));
        } else {
            onChange([...selectedValues, optionValue]);
        }
    };

    return (
        <>
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search" />
            <hr className="my-3 -mx-2" />
            {filteredOptions.length > 0 && (
                <fieldset className="border-0 p-0 m-0">
                    <legend className="sr-only">Select options</legend>
                    {filteredOptions.map(option => (
                        <Label key={option.value} className="!mb-0 h-8">
                            <Checkbox
                                name="checkbox-editor"
                                id={`checkbox-editor-option-${option.value}`}
                                value={option.value}
                                checked={selectedValues.includes(option.value)}
                                onChange={() => handleCheckboxChange(option.value)}
                                className="!mr-1 !my-1"
                            />
                            <span className="leading-8">{option.label}</span>
                        </Label>
                    ))}
                </fieldset>
            )}
            {filteredOptions.length === 0 && <p className="text-center m-0 leading-8">No options found</p>}
        </>
    );
}

/**
 * Component to render a custom editor for a study person's attribute based on its type and definition.
 *
 * @param {Object} props - The props passed to the component.
 * @param {Array|string|null} props.value - The current value of the custom attribute.
 * @param {() => void} props.onChange - Callback function to handle changes to the value.
 * @param {() => void} props.onSave - Callback function triggered to save the value.
 * @param {{
 *     type?: string
 *     definition?: Array<{
 *         value: string | number
 *         label: string
 *     }>
 * }} props.customStudyPersonAttribute - Information about the custom study person attribute, including type and definitions.
 *
 * @return {React.ReactNode}
 */
function CustomStudyPersonAttributeEditor({ value, onChange, onSave, customStudyPersonAttribute }) {
    /**
     * @type {[]|{value: string, label: string}[]}
     */
    const options = useMemo(() => {
        if (!customStudyPersonAttribute.hasOwnProperty('definition')) return [];

        const options = customStudyPersonAttribute.definition.map(def => ({
            value: def.value,
            label: def.label
        }));

        // If dropdown then add a null value on the start
        if (customStudyPersonAttribute.type === 'dropdown') {
            options.unshift({
                value: '',
                label: 'No value'
            });
        }

        return options;
    }, [customStudyPersonAttribute]);

    if (!customStudyPersonAttribute.hasOwnProperty('type'))
        return <TextEditor value={value} onChange={onChange} onSave={onSave} placeholder="Enter value" />;

    if (customStudyPersonAttribute.type === 'dropdown')
        return <RadioEditor value={value} onChange={onChange} options={options} />;

    if (customStudyPersonAttribute.type === 'checkbox')
        return <CheckboxEditor value={value} onChange={onChange} options={options} />;

    return null;
}

function CheckDropdown({
    checkAllOrVisibleStatus,
    deselectAll,
    setShowModalSelectCount,
    handleCheckAllDropdownChange,
    visibleStudyPeopleCount,
    totalStudyPeopleCount
}) {
    const [showCheckAllDropdown, setShowCheckAllDropdown] = useState(false);
    const ref = useRef(null);

    const countBadge = count => <span className="label-highlight-number">{helpers.numberFormat(count)}</span>;

    const customCountItem = {
        id: TABLE_CHECKED_STATUS.CUSTOM,
        title: TABLE_CHECKED_STATUS_LABEL.CUSTOM
    };

    // When every result is already rendered (nothing is paginated out of view),
    // "visible" and "all" select the same set. Collapse to a single option that
    // uses VISIBLE so individuals can still be deselected.
    const allResultsVisible = totalStudyPeopleCount > 0 && visibleStudyPeopleCount >= totalStudyPeopleCount;
    const items = allResultsVisible
        ? [
              {
                  id: TABLE_CHECKED_STATUS.VISIBLE,
                  title: TABLE_CHECKED_STATUS_LABEL.ALL,
                  suffix: countBadge(visibleStudyPeopleCount)
              },
              customCountItem
          ]
        : [
              {
                  id: TABLE_CHECKED_STATUS.ALL,
                  title: TABLE_CHECKED_STATUS_LABEL.ALL,
                  suffix: countBadge(totalStudyPeopleCount)
              },
              {
                  id: TABLE_CHECKED_STATUS.VISIBLE,
                  title: TABLE_CHECKED_STATUS_LABEL.VISIBLE,
                  suffix: countBadge(visibleStudyPeopleCount)
              },
              customCountItem
          ];

    return (
        <>
            {checkAllOrVisibleStatus === TABLE_CHECKED_STATUS.NONE && (
                <Button
                    ref={ref}
                    variant="transparent-icon-nobg"
                    data-testid="check-all-dropdown-button"
                    mr={2}
                    style={{
                        position: 'absolute',
                        top: '6px',
                        left: '-8px'
                    }}
                    onClick={e => {
                        e.stopPropagation();
                        setShowCheckAllDropdown(!showCheckAllDropdown);
                    }}
                >
                    <FiCheckSquare />
                </Button>
            )}
            {checkAllOrVisibleStatus !== TABLE_CHECKED_STATUS.NONE && (
                <Button
                    variant="small-icon"
                    data-testid="deselect-all-dropdown-button"
                    mr={2}
                    style={{
                        position: 'absolute',
                        top: '12px',
                        left: '0',
                        border: `1px solid #ffa040`,
                        color: `#ffa040`
                    }}
                    onClick={e => {
                        e.stopPropagation();
                        deselectAll();
                    }}
                >
                    <FiMinus />
                </Button>
            )}
            <Portal isOpen={showCheckAllDropdown}>
                <PositionerV2 anchorRef={ref}>
                    <NiceDropdown
                        style={{ inset: 'auto', position: 'static' }}
                        width="240px"
                        onClose={() => {
                            setShowCheckAllDropdown(false);
                        }}
                        items={items}
                        onChange={(value, event) => {
                            // Don't know why it's necessary but is
                            // It stops from using onSort when clicking any of option
                            event.stopPropagation();

                            if (value === TABLE_CHECKED_STATUS.CUSTOM) {
                                setShowModalSelectCount(true);
                            } else {
                                handleCheckAllDropdownChange(value);
                            }
                        }}
                    />
                </PositionerV2>
            </Portal>
        </>
    );
}

function ScreenerAnsweredStatus({ status, studyPersonId, screenerId }) {
    const tooltip_id = `screener-answered-tooltip-${studyPersonId}-${screenerId}`;
    const icon_style = { margin: '0 8px 1px 0' };
    let icon = null;
    let message = '';
    switch (status) {
        case 'qualified':
            icon = <FiCheckCircle data-tip data-for={tooltip_id} className="fs-16 color-success" style={icon_style} />;
            message = 'Qualified';
            break;
        case 'disqualified':
            icon = <FiXCircle data-tip data-for={tooltip_id} className="fs-16 color-danger" style={icon_style} />;
            message = 'Disqualified';
            break;
        case 'over_quota':
            icon = <FiCheckCircle data-tip data-for={tooltip_id} className="fs-16 color-warning" style={icon_style} />;
            message = 'Qualified, Over Quota';
            break;
        case 'incomplete':
            icon = <FiAlertCircle data-tip data-for={tooltip_id} className="fs-16 color-warning" style={icon_style} />;
            message = 'Incomplete';
            break;
    }

    return (
        <>
            {icon}
            <ReactTooltip id={tooltip_id} place="left" effect="solid">
                {message}
            </ReactTooltip>
        </>
    );
}

const useStudyParticipantsTable = ({
    onSort,
    sort,
    setShowModalSelectCount,
    setShowModalQualtricsUiDatacenter,
    showCheckAllDropdown,
    checkAllOrVisibleStatus,
    setShowCheckAllDropdown,
    showHideColumns,
    study,
    sortObject,
    setSortObject,
    deselectAll,
    handleCheckAllDropdownChange,
    visibleStudyPeopleCount,
    totalStudyPeopleCount,
    adminFacing,
    showParticipantMoreDropdownSpId,
    setShowParticipantMoreDropdownSpId,
    auth,
    p_checked,
    onClickParticipant,
    onCheckParticipant,
    onSearchKeywordChange,
    onStudyPersonUpdate
}) => {
    const { addSuccessToast, addErrorToast } = useToastHelper();
    const questions = useMemo(() => {
        return helpersStudy.getAllScreenerQuestionsList(study.screener, study.screeners);
    }, [study.screeners, study.screener]);
    const { data: collaborators, isLoading: isCollaboratorsLoading } = useQuery({
        queryFn: () => service_studies.studyGetUsers(study.id),
        onError: (_, errorText) => {
            addErrorToast(errorText);
        },
        skip: !study.id
    });
    const { mutate: assignUser } = useMutation({
        mutationFn: ({ studyPerson, userId }) =>
            service_studies.assignUserToPeopleInStudy(study.id, [{ id: studyPerson.person_id }], userId, {}, false),
        onSuccess: (_, { studyPerson, userId }) => {
            addSuccessToast('User assigned to study');

            const collaborator = collaborators.find(collaborator => collaborator.user_id === userId);
            onStudyPersonUpdate({
                ...studyPerson,
                assigned_user_id: collaborator ? collaborator.user_id : null,
                assigned_user: collaborator
                    ? { id: userId, avatar: collaborator.user.avatar, name: collaborator.user.name }
                    : null
            });
        },
        onError: (_, errorText) => {
            addErrorToast(errorText);
        }
    });
    const { mutate: setSession } = useMutation({
        mutationFn: ({ studyPerson, spot }) => service_studies.updateStudyPersonSpot(study.id, studyPerson.id, spot),
        onSuccess: (_, { studyPerson, spot }) => {
            addSuccessToast('Session updated');
            onStudyPersonUpdate({ ...studyPerson, spot });
        },
        onError: (_, errorText) => {
            addErrorToast(errorText);
        }
    });
    const { mutate: cancelSession } = useMutation({
        mutationFn: ({ studyPerson }) => service_studies.cancelStudyPersonSpot(study.id, studyPerson.id),
        onSuccess: (_, { studyPerson }) => {
            addSuccessToast('Session cancelled');
            onStudyPersonUpdate({ ...studyPerson, spot: null });
        },
        onError: (_, errorText) => {
            addErrorToast(errorText);
        }
    });
    const { mutate: confirmSession } = useMutation({
        mutationFn: ({ studyPerson, confirmationAcceptedAt }) =>
            service_studies.confirmStudyPersonSession(study.id, studyPerson.id, { confirmationAcceptedAt }),
        onSuccess: (_, { studyPerson, confirmationAcceptedAt }) => {
            addSuccessToast('Session confirmed');
            onStudyPersonUpdate({ ...studyPerson, confirmation_accepted_at: confirmationAcceptedAt });
        },
        onError: (_, errorText) => {
            addErrorToast(errorText);
        }
    });
    const { mutate: updateLocation } = useMutation({
        mutationFn: ({ studyPerson, location }) =>
            service_studies.updateStudyPersonLocation(study.id, studyPerson.id, location),
        onSuccess: (_, { studyPerson, location }) => {
            addSuccessToast('Location updated');
            onStudyPersonUpdate({ ...studyPerson, custom_location: location });
        },
        onError: (_, errorText) => {
            addErrorToast(errorText);
        }
    });
    const { mutate: pay } = useMutation({
        mutationFn: ({ studyPerson, data: { paymentType, amount, paymentDate } }) =>
            service_studies.bulkPayAccept(study.id, [studyPerson.person_id], paymentType, amount, paymentDate),
        onSuccess: (_, { studyPerson, data: { paymentType, amount, paymentDate } }) => {
            addSuccessToast('Payment accepted');
            onStudyPersonUpdate({
                ...studyPerson,
                incentive_receipt: paymentType,
                incentive_total_paid: amount,
                incentive_status: STUDY_PERSON_INCENTIVE_STATUS.PAID,
                incentive_sent_at: paymentDate,
                incentive_currency: study.incentive_currency
            });
        },
        onError: (_, errorText) => {
            addErrorToast(errorText);
        }
    });
    const { mutate: payManually } = useMutation({
        mutationFn: ({ studyPerson, data }) => service_studies.updateManualPayment(study.id, studyPerson.id, data),
        onSuccess: (_, { studyPerson, data: { paymentDate, amount } }) => {
            const isPaid = !!+amount;

            const unpaidData = {
                incentive_receipt: null,
                incentive_total_paid: null,
                incentive_status: null,
                incentive_sent_at: null,
                incentive_currency: null
            };

            const paidData = {
                incentive_total_paid: amount,
                incentive_sent_at: paymentDate
            };

            addSuccessToast('Payment updated');
            onStudyPersonUpdate({
                ...studyPerson,
                ...(!isPaid ? unpaidData : paidData)
            });
        },
        onError: (_, errorText) => {
            addErrorToast(errorText);
        }
    });
    const { mutate: updateStudyPerson } = useMutation({
        mutationFn: ({ studyPerson }) => service_studies.updateStudyPerson(study.id, studyPerson.id, studyPerson),
        onSuccess: (_, { studyPerson }) => {
            addSuccessToast('Study person updated');
            onStudyPersonUpdate(studyPerson);
        },
        onError: (_, errorText) => {
            addErrorToast(errorText);
        }
    });
    const { mutate: updateStudyCustomVariable } = useMutation({
        mutationFn: ({ studyPerson, payload }) =>
            service_studies.updateStudyPersonCustomAttribute(study.id, studyPerson.id, payload),
        onSuccess: (_, { studyPerson, payload }) => {
            addSuccessToast('Custom attribute updated');

            const updatedStudyPerson = produce(studyPerson, draft => {
                const customAttribute = draft.custom_attributes.find(
                    custom_attribute => custom_attribute.sp_attribute_id === payload.customAttributeId
                );

                if (customAttribute) {
                    customAttribute.value = payload.customAttributeValue;
                } else {
                    const newCustomAttribute = {
                        created_at: new Date().toISOString(),
                        id: null,
                        sp_attribute_id: payload.customAttributeId,
                        study_id: study.id,
                        study_person_id: studyPerson.id,
                        updated_at: new Date().toISOString(),
                        value: payload.customAttributeValue
                    };

                    draft.custom_attributes.push(newCustomAttribute);
                }
            });

            onStudyPersonUpdate(updatedStudyPerson);
        },
        onError: (_, errorText) => {
            addErrorToast(errorText);
        }
    });

    const shouldShowColumn = useCallback(
        columnId => {
            return showHideColumns.find(column => column.id === columnId && column.checked);
        },
        [showHideColumns]
    );

    const getColumnOrder = useCallback(
        columnId => {
            const found = showHideColumns.find(column => column.id === columnId);
            return found ? found.order : 0;
        },
        [showHideColumns]
    );

    const getScreenerById = useCallback(
        study_screener_id => {
            if (study_screener_id && study.screeners) {
                const found_screener = study.screeners.find(s => s.id === study_screener_id);
                if (found_screener) {
                    return found_screener;
                }
            }
        },
        [study]
    );

    const onSearchIp = ip => {
        if (!onSearchKeywordChange) return;

        onSearchKeywordChange({
            target: {
                value: 'ip:' + ip
            }
        });
        addSuccessToast('Quick Search updated with IP address');
        analytics.track('study-click_duplicate_ip');
    };

    const getScreenersList = useCallback(
        studyPerson => {
            const list = [];
            if (study.screener && study.screener[0] && study.screener[0].length) {
                const oldScreenerLink = helpersScreener.getScreenerLink(study.uuid, auth.account, {
                    defaultDomain: true
                });
                list.push({
                    id: 'default',
                    title: `Open screener: Default`,
                    icon: <FiFileText />,
                    onClick: () => {
                        window.open(oldScreenerLink);
                    }
                });
            }
            if (study.screeners && study.screeners.length) {
                study.screeners.forEach(screener => {
                    const screener_link = helpersScreener.getScreenerLinkNewForPerson(
                        screener.uuid,
                        auth.account,
                        studyPerson,
                        { defaultDomain: true }
                    );
                    const foundScreenerAnswer = studyPerson.screener_answers.find(
                        screenerAnswer => screenerAnswer.study_screener_id === screener.id
                    );

                    const isMissingQualtricsUiDatacenterId =
                        foundScreenerAnswer &&
                        helpersScreener.isQualtricsConnected(screener) &&
                        !auth.account.qualtrics_ui_datacenter_id;

                    list.push({
                        id: screener.id,
                        title: `${foundScreenerAnswer ? 'View answers' : 'Open screener'}: ${screener.title}`,
                        icon: foundScreenerAnswer ? <FiFileText /> : <FiFile />,
                        onClick: () => {
                            if (isMissingQualtricsUiDatacenterId) {
                                setShowModalQualtricsUiDatacenter(true);
                            } else {
                                window.open(screener_link);
                            }
                        }
                    });
                });
            }
            return list;
        },
        [study, auth]
    );

    const getTooltip = useCallback(
        studyPerson => {
            const tooltipInfo = [];
            let isDanger = false;
            const isOptedOut = helpers.personIsOptedOut(studyPerson.person);

            const emailStatusLabels = helpers.getEmailStatusLabels(studyPerson.person);
            if (emailStatusLabels) {
                tooltipInfo.push(emailStatusLabels);
            }

            // check if latest email to respondent failed
            if (helpersStudy.didLatestEmailFail(studyPerson.emails_sent)) {
                isDanger = true;
                tooltipInfo.push(<>Latest email failed to send, view profile for more details.</>);
            }

            if (isOptedOut) {
                tooltipInfo.push('Opt-out from email communication');
            }

            if (
                auth.account.settings.is_double_opt_in_enabled &&
                !isOptedOut &&
                !studyPerson.person.is_opt_in_confirmed
            ) {
                tooltipInfo.push('Opt-in is not confirmed');
            }

            if (helpers.personIsPurchasedList(studyPerson.person)) {
                tooltipInfo.push('Purchased list - email sending not supported');
            }

            if (studyPerson.screener_answers) {
                studyPerson.screener_answers.forEach(screenerAnswer => {
                    const screener = getScreenerById(screenerAnswer.study_screener_id);
                    if (helpersStudy.shouldShowDupeIpWarning(screener, screenerAnswer)) {
                        isDanger = true;
                        tooltipInfo.push(
                            <>
                                Duplicate IP (
                                <span className="link" onClick={() => onSearchIp(screenerAnswer.ip)}>
                                    {screenerAnswer.ip}
                                </span>
                                ) across responses for "{screener.title}"{' '}
                                <a
                                    href="https://help.dscout.com/private-panels/studies/duplicate-ip-protection"
                                    target="_blank"
                                    className="help-question"
                                >
                                    ?
                                </a>
                            </>
                        );
                    }
                });
            }

            if (!tooltipInfo.length) {
                return [null, false];
            }

            const tooltip = (
                <ReactTooltip
                    id={`sp-tooltip-${studyPerson.id}`}
                    effect="solid"
                    place="right"
                    className="react-tooltip-hover-stay"
                    delayHide={500}
                >
                    <ul className="tight">
                        {tooltipInfo.map((ti, index) => (
                            <li key={index}>{ti}</li>
                        ))}
                    </ul>
                </ReactTooltip>
            );

            return [tooltip, isDanger];
        },
        [getScreenerById, auth]
    );

    const getPersonIdentifier = useCallback(
        (studyPerson, displayTooltip, isDangerTooltip) => {
            let identifier = (
                <ProtectedFieldComposite
                    person={studyPerson.person}
                    classNames={{
                        root: 'protected-field-one-link'
                    }}
                    empty={
                        <PersonLink
                            name={<span className="text-secondary-dark">Name missing</span>}
                            onClick={() => onClickParticipant(studyPerson)}
                        />
                    }
                    fields={[
                        {
                            dataKey: COL_FIRST_NAME,
                            hideable: true,
                            empty: '-',
                            renderLayout: node => (
                                <span className="link muted va-top strong">
                                    <PersonLink name={node} onClick={() => onClickParticipant(studyPerson)} />
                                </span>
                            ),
                            labels: {
                                unmask: 'Unmask first name'
                            }
                        },
                        {
                            dataKey: COL_LAST_NAME,
                            hideable: true,
                            empty: '-',
                            renderLayout: node => (
                                <span className="link muted va-top strong">
                                    <PersonLink name={node} onClick={() => onClickParticipant(studyPerson)} />
                                </span>
                            ),
                            labels: {
                                unmask: 'Unmask last name'
                            }
                        }
                    ]}
                    hideable
                />
            );

            if (displayTooltip) {
                identifier = (
                    <>
                        <FiAlertCircle
                            data-tip
                            data-for={`sp-tooltip-${studyPerson.id}`}
                            style={{ margin: '4px 4px 0 0', minWidth: '14px' }}
                            className={`va-top ${isDangerTooltip ? 'red' : 'gray'}`}
                        />{' '}
                        {identifier}
                    </>
                );
            }

            // keep this last
            if (helpersStudy.isSpLocked(studyPerson)) {
                identifier = (
                    <>
                        <FiEye style={{ verticalAlign: 'top', margin: '5px 8px 0 0' }} />
                        {identifier}
                    </>
                );
            }

            return identifier;
        },
        [auth, onClickParticipant]
    );

    const getAllStudyPersonAnswers = useCallback(studyPerson => {
        let allStudyPersonAnswers = [];

        if (
            studyPerson.screener_answers &&
            studyPerson.screener_answers instanceof Array &&
            studyPerson.screener_answers.length
        ) {
            studyPerson.screener_answers.forEach(single_screener => {
                const sa = JSON.parse(single_screener.answers);
                if (sa) {
                    allStudyPersonAnswers = allStudyPersonAnswers.concat(helpersStudy.getAllScreenerAnswersList(sa));
                }
            });
        }

        return allStudyPersonAnswers;
    }, []);

    const renderStudyPersonCell = useCallback(
        studyPerson => {
            const [tooltip, isDangerTooltip] = getTooltip(studyPerson);

            return (
                <>
                    <div className="relative">
                        {adminFacing && (
                            <div className="study-person-more-hover-parent">
                                <Button
                                    data-testid="study-person-view-button"
                                    type="button"
                                    variant="secondary"
                                    mr={1}
                                    ml={2}
                                    className="va-top secondary-tiny study-person-more-hover"
                                    onClick={e => {
                                        e.preventDefault();
                                        onClickParticipant(studyPerson);
                                    }}
                                >
                                    <FiSidebar /> View
                                </Button>
                                <ScreenersDropdown
                                    showParticipantMoreDropdownSpId={showParticipantMoreDropdownSpId}
                                    setShowParticipantMoreDropdownSpId={setShowParticipantMoreDropdownSpId}
                                    items={getScreenersList(studyPerson)}
                                    studyPerson={studyPerson}
                                />
                            </div>
                        )}
                        <input
                            type="checkbox"
                            key={`checkbox-${studyPerson.person_id}`}
                            data-personid={studyPerson.person_id}
                            // `some`, not `find` — `find` returns undefined when the row is
                            // unchecked, which React reads as "no `checked` prop" and silently
                            // switches the input to uncontrolled. The box then keeps whatever
                            // the DOM last had, so "Deselect all" left rows visibly ticked
                            // while the selection was already empty (PFOX-1315).
                            // person.id can be string! so we need to use == instead of ===
                            checked={p_checked.some(person => person.id == studyPerson.person_id)}
                            onChange={() => {
                                onCheckParticipant(studyPerson.person_id);
                            }}
                            name="person_id"
                            style={{
                                position: 'absolute',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                left: '-15px'
                            }}
                            onClick={e => {
                                e.stopPropagation();
                            }}
                            disabled={['all', 'custom_count'].includes(checkAllOrVisibleStatus)}
                        />
                        <Box ml="8px">
                            <Box className="ellipsis">
                                <Flex>{getPersonIdentifier(studyPerson, tooltip !== null, isDangerTooltip)}</Flex>
                            </Box>
                            <ParticipantStatusLabel studyPerson={studyPerson} />
                        </Box>
                    </div>
                    <Portal>{tooltip}</Portal>
                </>
            );
        },
        [
            getTooltip,
            getPersonIdentifier,
            onCheckParticipant,
            showParticipantMoreDropdownSpId,
            setShowParticipantMoreDropdownSpId,
            getScreenersList,
            p_checked,
            checkAllOrVisibleStatus,
            adminFacing,
            onClickParticipant
        ]
    );

    // Execution of mapQuestionsToAnswers takes a lot of time when we sum all cells.
    // To make it faster, we use simple caching where the key is study person id
    // Far all cells the result is the same that's way is safe to use
    const cache = useRef({});
    const getQuestionsToAnswers = useCallback(
        studyPerson => {
            let questionAnswerMap;

            if (!cache.current[studyPerson.id]) {
                questionAnswerMap = helpersStudy.mapQuestionsToAnswers(
                    questions,
                    getAllStudyPersonAnswers(studyPerson)
                );
                cache.current[studyPerson.id] = questionAnswerMap;
            } else {
                questionAnswerMap = cache.current[studyPerson.id];
            }

            return questionAnswerMap;
        },
        [questions, getAllStudyPersonAnswers]
    );

    const renderQuestionCell = useCallback(
        (studyPerson, question) => {
            const question_answer_map = getQuestionsToAnswers(studyPerson);

            let answers_readable;
            let local_screener_answer = question_answer_map[question.field_name];

            if (Array.isArray(local_screener_answer)) {
                answers_readable = local_screener_answer.join(', ');

                // if its a date
                if (question.element === 'DatePicker') {
                    answers_readable = renderDate(answers_readable, {
                        timezone: auth?.user?.timezone,
                        displayFormat: question.date_display_format || DATE_DISPLAY_FORMAT.ABSOLUTE,
                        includeTime: question.showTimeSelect,
                        onlyTime: question.showTimeSelectOnly
                    });
                } else if (question.element === 'Signature') {
                    if (local_screener_answer && local_screener_answer.length && local_screener_answer[0]) {
                        answers_readable = 'Signed';
                    } else {
                        answers_readable = 'Not Signed';
                    }
                } else if (question.element === 'FileUpload') {
                    answers_readable =
                        local_screener_answer && local_screener_answer.length && local_screener_answer[0]
                            ? helpers.renderLinksForUploadedScreenerFile(local_screener_answer[0])
                            : '';
                } else if (question.element === 'AddPipeInput') {
                    const video_link = helpersScreener.getVideoLinkFromScreenerAnswer(local_screener_answer);
                    if (video_link) {
                        answers_readable = (
                            <a href={video_link} target="_blank">
                                View video
                            </a>
                        );
                    }
                } else if (question.element === 'Captcha') {
                    answers_readable =
                        local_screener_answer && local_screener_answer.length && local_screener_answer[0]
                            ? helpersScreener.getCaptchaValueHumanReadable(local_screener_answer[0])
                            : '';
                }

                // otherwise just keep the value
            } else {
                if (question.element === 'DatePicker') {
                    answers_readable = renderDate(local_screener_answer, {
                        timezone: auth?.user?.timezone,
                        displayFormat: question.date_display_format || DATE_DISPLAY_FORMAT.ABSOLUTE,
                        includeTime: question.showTimeSelect,
                        onlyTime: question.showTimeSelectOnly
                    });
                } else {
                    answers_readable = local_screener_answer;
                }
            }

            return answers_readable;
        },
        [getQuestionsToAnswers]
    );

    const renderScreenerCustomVariableCell = useCallback((studyPerson, screener) => {
        const singleScreenerAnswer = studyPerson.screener_answers.find(
            screenerAnswer => screenerAnswer.study_screener_id === screener.id
        );

        if (!singleScreenerAnswer) return null;

        let cv_row;
        if (singleScreenerAnswer.custom_variables && Object.keys(singleScreenerAnswer.custom_variables)) {
            cv_row = Object.entries(singleScreenerAnswer.custom_variables)
                .map(([key, value]) => `${key}: ${value}`)
                .join(', ');
        }

        return cv_row;
    }, []);

    const renderScreenerAnswerCell = useCallback(
        (studyPerson, screener) => {
            let s_a_element = null;
            let screener_answer_found = null;
            if (studyPerson.screener_answers && studyPerson.screener_answers instanceof Array) {
                screener_answer_found = studyPerson.screener_answers.find(sa => sa.study_screener_id == screener.id);
                if (screener_answer_found) {
                    const status = screener_answer_found.incomplete
                        ? 'incomplete'
                        : screener_answer_found.is_qualified == IS_QUALIFIED.YES
                        ? 'qualified'
                        : screener_answer_found.is_qualified == IS_QUALIFIED.OVER_QUOTA
                        ? 'over_quota'
                        : 'disqualified';
                    s_a_element = (
                        <>
                            <ScreenerAnsweredStatus
                                status={status}
                                studyPersonId={studyPerson.id}
                                screenerId={screener.id}
                            />
                            {helpersStudy.displayStudyDate(screener_answer_found.updated_at, study)}
                            {' · '}
                            <GeoIndicator countryCode={screener_answer_found.country_code} />
                        </>
                    );
                }
            }
            if (!screener_answer_found) {
                s_a_element = '';
            }

            return s_a_element;
        },
        [study]
    );

    // TODO: move it to Component
    const renderStudyPersonCustomAttr = useCallback((studyPerson, cspa) => {
        const found = studyPerson.custom_attributes.find(attr => attr.sp_attribute_id == cspa.id);

        let render_val = '';
        if (found) {
            if (cspa.type && cspa.type === 'checkbox') {
                if (found.value && helpers.isJsonString(found.value)) {
                    const render_val_arr = [];

                    JSON.parse(found.value).forEach(v => {
                        const cspa_d_found = cspa.definition.find(d => d && d.value == v);
                        if (cspa_d_found) {
                            render_val_arr.push(cspa_d_found.label);
                        }
                    });
                    render_val = render_val_arr.join(', ');
                }
            } else if (cspa.type && cspa.type === 'dropdown') {
                const cspa_d_found = cspa.definition.find(d => d && d.value == found.value);
                if (cspa_d_found) {
                    render_val = cspa_d_found.label;
                }
            } else {
                render_val = found.value;
            }
        }

        return render_val;
    }, []);

    /**
     * Retrieves the value of a custom attribute for a specific study person.
     *
     * @function
     * @param {{
     *     custom_attributes: Array<{
     *         sp_attribute_id: string,
     *         value: string
     *     }>
     * }} studyPerson - The study person object containing custom attributes.
     * @param {{
     *     id: string
     *     type?: string
     * }} customStudyPersonAttribute - The custom attribute definition object, containing the identifier and type of the attribute.
     * @returns {string|Array|null} - The value of the custom attribute. Returns:
     *   - A string if the type is "dropdown" or unspecified.
     *   - An array if the type is "checkbox" and the value can be parsed as JSON.
     *   - Null if no matching custom attribute is found, if the `type` is not supported, or in case of a parsing error.
     */
    const getStudyPersonCustomAttrValue = useCallback((studyPerson, customStudyPersonAttribute) => {
        const customAttribute = studyPerson.custom_attributes.find(
            customAttribute => customStudyPersonAttribute.id === customAttribute.sp_attribute_id
        );

        if (!customAttribute) return null;

        if (!customStudyPersonAttribute.hasOwnProperty('type')) return customAttribute.value;

        if (customStudyPersonAttribute.type === 'checkbox') {
            try {
                return JSON.parse(customAttribute.value);
            } catch (error) {
                return [];
            }
        }

        if (customStudyPersonAttribute.type === 'dropdown') {
            return customAttribute.value;
        }

        return null;
    }, []);

    const compactRender = useProtectedCompactRender();

    return useMemo(() => {
        const columns = [
            {
                key: 'col-sticky-name',
                order: -1,
                width: '300px',
                minWidth: 200,
                headerProps: {
                    style: {
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        minWidth: '240px',
                        paddingLeft: '24px'
                    },
                    className: 'sticky-row-style'
                },
                onCell: () => ({
                    style: {
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        minWidth: '240px'
                    },
                    className: 'sticky-row-style'
                }),
                title: (
                    <>
                        <CheckDropdown
                            checkAllOrVisibleStatus={checkAllOrVisibleStatus}
                            deselectAll={deselectAll}
                            handleCheckAllDropdownChange={handleCheckAllDropdownChange}
                            setShowModalSelectCount={setShowModalSelectCount}
                            visibleStudyPeopleCount={visibleStudyPeopleCount}
                            totalStudyPeopleCount={totalStudyPeopleCount}
                        />
                        Full Name
                    </>
                ),
                sorter: true,
                sortOrder: 'sp' === sortObject.group && 'name' === sortObject.column ? sortObject.direction : null,
                onSort: () => onSort({ sort_group: 'sp', sort_column: 'name', default_sort_direction: 'asc' }),
                render: renderStudyPersonCell
            }
        ];

        if (shouldShowColumn(STUDY_TABLE_COLUMN_ASSIGNED.id)) {
            columns.push({
                key: STUDY_TABLE_COLUMN_ASSIGNED.id,
                order: getColumnOrder(STUDY_TABLE_COLUMN_ASSIGNED.id),
                title: 'Assigned',
                sorter: true,
                editable: true,
                editRender: ({ value, onChange }) => (
                    <AssignedUserEditor
                        value={value}
                        onChange={onChange}
                        collaborators={collaborators}
                        isCollaboratorsLoading={isCollaboratorsLoading}
                    />
                ),
                sortOrder: 'sp' === sortObject.group && 'assigned' === sortObject.column ? sortObject.direction : null,
                onSort: () => onSort({ sort_group: 'sp', sort_column: 'assigned' }),
                width: COL_WIDTH,
                minWidth: COL_MIN_WIDTH,
                headerProps: {
                    className: 'ellipsis'
                },
                onCell: studyPerson => ({
                    className: 'ellipsis',
                    value: studyPerson.assigned_user ? studyPerson.assigned_user.id : null,
                    onChange: userId => assignUser({ studyPerson, userId: userId || -1 }),
                    onDelete: () => assignUser({ studyPerson, userId: -1 })
                }),
                render: studyPerson => {
                    if (!(studyPerson && studyPerson.assigned_user && studyPerson.assigned_user.id)) {
                        return '';
                    }

                    if (studyPerson.assigned_user.avatar)
                        return (
                            <>
                                <img
                                    src={studyPerson.assigned_user.avatar}
                                    alt="Assigned user avatar"
                                    style={avatar_style}
                                    className="circle va-top"
                                />
                                {studyPerson.assigned_user.name}
                            </>
                        );

                    return (
                        <>
                            <FiUser style={avatar_style} className="circle va-top" />
                            {studyPerson.assigned_user.name}
                        </>
                    );
                }
            });
        }

        if (shouldShowColumn(STUDY_TABLE_COLUMN_SPOT.id)) {
            columns.push({
                key: STUDY_TABLE_COLUMN_SPOT.id,
                order: getColumnOrder(STUDY_TABLE_COLUMN_SPOT.id),
                title: 'Session',
                sorter: true,
                sortOrder: 'sp' === sortObject.group && 'spot' === sortObject.column ? sortObject.direction : null,
                onSort: () => onSort({ sort_group: 'sp', sort_column: 'spot' }),
                width: COL_WIDTH,
                minWidth: COL_MIN_WIDTH,
                headerProps: {
                    className: 'ellipsis'
                },
                editable: true,
                editRender: ({ value, onChange }, { setLocked }) => (
                    <SessionEditor value={value} onChange={onChange} study={study} setLocked={setLocked} />
                ),
                onCell: studyPerson => ({
                    className: 'ellipsis',
                    value: studyPerson.spot,
                    onChange: spot => setSession({ spot, studyPerson }),
                    onDelete: () => cancelSession({ studyPerson })
                }),
                render: studyPerson => {
                    return helpersStudy.displayStudyPersonSpot(studyPerson, study);
                }
            });
        }

        if (shouldShowColumn(STUDY_TABLE_COLUMN_CONFIRMED.id)) {
            columns.push({
                key: STUDY_TABLE_COLUMN_CONFIRMED.id,
                order: getColumnOrder(STUDY_TABLE_COLUMN_CONFIRMED.id),
                title: 'Confirmed',
                sorter: true,
                sortOrder: 'sp' === sortObject.group && 'confirmed' === sortObject.column ? sortObject.direction : null,
                onSort: () => onSort({ sort_group: 'sp', sort_column: 'confirmed' }),
                width: COL_WIDTH,
                minWidth: COL_MIN_WIDTH,
                headerProps: {
                    className: 'ellipsis'
                },
                editable: true,
                editRender: ({ value, onChange }) => (
                    <DateEditor onChange={onChange} value={value} timezone={study.timezone} />
                ),
                onCell: studyPerson => ({
                    value: studyPerson.confirmation_accepted_at,
                    onChange: confirmationAcceptedAt => confirmSession({ studyPerson, confirmationAcceptedAt }),
                    onDelete: () => confirmSession({ studyPerson, confirmationAcceptedAt: null }),
                    className: 'ellipsis'
                }),
                render: studyPerson => helpersStudy.displayConfirmedAt(studyPerson, study)
            });
        }

        if (shouldShowColumn(STUDY_TABLE_COLUMN_LOCATION.id)) {
            columns.push({
                key: STUDY_TABLE_COLUMN_LOCATION.id,
                order: getColumnOrder(STUDY_TABLE_COLUMN_LOCATION.id),
                title: 'Location',
                sorter: true,
                sortOrder: 'sp' === sortObject.group && 'location' === sortObject.column ? sortObject.direction : null,
                onSort: () => onSort({ sort_group: 'sp', sort_column: 'location' }),
                width: COL_WIDTH,
                minWidth: COL_MIN_WIDTH,
                editable: !study.location_type,
                editRender: ({ value, onChange, onSave }) => (
                    <TextEditor value={value} onChange={onChange} placeholder="Location" onSave={onSave} />
                ),
                headerProps: {
                    className: 'ellipsis'
                },
                onCell: studyPerson => ({
                    className: 'ellipsis',
                    value: studyPerson.custom_location,
                    onChange: location => updateLocation({ studyPerson, location }),
                    onDelete: () => updateLocation({ studyPerson, location: null })
                }),
                render: studyPerson => {
                    const location = helpers.getStudyPersonLocation(studyPerson, study);
                    const hasRecordings = studyPerson.session_recordings?.length > 0;
                    if (!hasRecordings) return location;
                    return (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span className="ellipsis" style={{ flex: '1 1 auto', minWidth: 0 }}>
                                {location}
                            </span>
                            <SessionRecordingsDropdown recordings={studyPerson.session_recordings} />
                        </div>
                    );
                }
            });
        }

        if (shouldShowColumn(STUDY_TABLE_COLUMN_PAID.id)) {
            columns.push({
                key: STUDY_TABLE_COLUMN_PAID.id,
                order: getColumnOrder(STUDY_TABLE_COLUMN_PAID.id),
                title: STUDY_TABLE_COLUMN_PAID.title,
                sorter: true,
                sortOrder: 'sp' === sortObject.group && 'paid' === sortObject.column ? sortObject.direction : null,
                onSort: () => onSort({ sort_group: 'sp', sort_column: 'paid' }),
                width: COL_WIDTH,
                minWidth: COL_MIN_WIDTH,
                headerProps: {
                    className: 'ellipsis'
                },
                editable: 'modal',
                editRender: ({ value: studyPerson, onCancel: onClose }) => {
                    const isEditable = checkIfPaymentEditable(studyPerson);

                    if (isEditable) {
                        return (
                            <ManualPaymentModal
                                isOpen
                                onClose={onClose}
                                title="Edit Payment"
                                person={studyPerson.person}
                                onSave={data => {
                                    return payManually(
                                        { studyPerson, data },
                                        {
                                            onSuccess: () => {
                                                onClose();
                                            }
                                        }
                                    );
                                }}
                                defaultValues={{
                                    amount: studyPerson.incentive_total_paid,
                                    date: studyPerson.incentive_sent_at
                                }}
                            />
                        );
                    }

                    // If study person is paid we have to return nothing
                    if (studyPerson.incentive_status === STUDY_PERSON_INCENTIVE_STATUS.PAID) return;

                    return (
                        <PaymentModal
                            auth={auth}
                            isOpen
                            onClose={onClose}
                            onPay={data =>
                                pay(
                                    { data, studyPerson },
                                    {
                                        onSuccess: () => {
                                            onClose();
                                        }
                                    }
                                )
                            }
                            title="Pay Incentive"
                            study={study}
                            payees={[studyPerson.person]}
                            defaultValues={{
                                amount: study.incentive
                            }}
                        />
                    );
                },
                onCell: studyPerson => ({
                    className: 'ellipsis',
                    value: studyPerson,
                    disableEditing:
                        studyPerson.incentive_status === STUDY_PERSON_INCENTIVE_STATUS.PAID &&
                        !checkIfPaymentEditable(studyPerson),
                    turnOffDeleting: true
                }),
                render: studyPerson => {
                    const displayState = getIncentiveDisplayState(studyPerson);

                    if (!displayState) return '';

                    const { type, label } = INCENTIVE_BADGE_CONFIG[displayState];
                    const amount = studyPerson.incentive_total_paid
                        ? ` ${helpers.renderCurrencyAmount(
                              studyPerson.incentive_currency,
                              studyPerson.incentive_total_paid
                          )}`
                        : '';

                    return (
                        <span className="flex items-center gap-1">
                            {amount}
                            <Badge type={type} size="sm">
                                {label}
                            </Badge>
                        </span>
                    );
                }
            });
        }

        if (shouldShowColumn(STUDY_TABLE_COLUMN_LAST_CONTACTED.id)) {
            columns.push({
                key: STUDY_TABLE_COLUMN_LAST_CONTACTED.id,
                order: getColumnOrder(STUDY_TABLE_COLUMN_LAST_CONTACTED.id),
                title: 'Last Contacted At',
                sorter: true,
                sortOrder:
                    'sp' === sortObject.group && 'last_contacted_at' === sortObject.column
                        ? sortObject.direction
                        : null,
                onSort: () => onSort({ sort_group: 'sp', sort_column: 'last_contacted_at' }),
                width: COL_WIDTH,
                minWidth: COL_MIN_WIDTH,
                headerProps: {
                    className: 'ellipsis'
                },
                onCell: () => ({
                    className: 'ellipsis'
                }),
                render: studyPerson => helpersStudy.displayStudyLastContactedAt(studyPerson, study)
            });
        }

        if (shouldShowColumn(STUDY_TABLE_COLUMN_NOTES.id)) {
            columns.push({
                key: STUDY_TABLE_COLUMN_NOTES.id,
                order: getColumnOrder(STUDY_TABLE_COLUMN_NOTES.id),
                title: STUDY_TABLE_COLUMN_NOTES.title,
                sorter: false,
                width: COL_WIDTH,
                minWidth: COL_MIN_WIDTH,
                headerProps: {
                    className: 'ellipsis'
                },
                editable: 'modal',
                editRender: ({ value, onChange, onSave, onCancel: onClose }) => (
                    <NotesEditorModal isOpen value={value} onChange={onChange} onSave={onSave} onClose={onClose} />
                ),
                onCell: studyPerson => ({
                    className: 'ellipsis',
                    value: studyPerson.notes,
                    onChange: notes =>
                        updateStudyPerson({
                            studyPerson: {
                                ...studyPerson,
                                notes
                            }
                        }),
                    onDelete: () =>
                        updateStudyPerson({
                            studyPerson: {
                                ...studyPerson,
                                notes: null
                            }
                        })
                }),
                render: studyPerson => <NotesCell notes={studyPerson.notes} />
            });
        }

        if (Array.isArray(study.custom_sp_attributes)) {
            study.custom_sp_attributes.forEach(cspa => {
                if (shouldShowColumn(helpersStudy.getCspaTableColumnId(cspa))) {
                    columns.push({
                        key: helpersStudy.getCspaTableColumnId(cspa),
                        order: getColumnOrder(helpersStudy.getCspaTableColumnId(cspa)),
                        title: cspa.title,
                        sorter: true,
                        width: COL_WIDTH,
                        minWidth: COL_MIN_WIDTH,
                        headerProps: {
                            className: 'ellipsis'
                        },
                        sortOrder:
                            'custom_study_attribute' === sortObject.group && cspa.id === sortObject.column
                                ? sortObject.direction
                                : null,
                        onSort: () => onSort({ sort_group: 'custom_study_attribute', sort_column: cspa.id }),
                        editable: true,
                        editRender: ({ value, onChange, onSave }) => (
                            <CustomStudyPersonAttributeEditor
                                customStudyPersonAttribute={cspa}
                                value={value}
                                onChange={onChange}
                                onSave={onSave}
                            />
                        ),
                        onCell: studyPerson => ({
                            className: 'ellipsis',
                            value: getStudyPersonCustomAttrValue(studyPerson, cspa),
                            onChange: value =>
                                updateStudyCustomVariable({
                                    studyPerson,
                                    payload: {
                                        customAttributeId: cspa.id,
                                        customAttributeValue: Array.isArray(value) ? JSON.stringify(value) : value
                                    }
                                }),
                            onDelete: () =>
                                updateStudyCustomVariable({
                                    studyPerson,
                                    payload: {
                                        customAttributeId: cspa.id,
                                        customAttributeValue: ''
                                    }
                                })
                        }),
                        render: studyPerson => renderStudyPersonCustomAttr(studyPerson, cspa)
                    });
                }
            });
        }

        if (Array.isArray(study.screeners)) {
            study.screeners.forEach(screener => {
                if (shouldShowColumn(helpersStudy.getScreenerAnsweredTableColumnId(screener.id))) {
                    columns.push({
                        key: helpersStudy.getScreenerAnsweredTableColumnId(screener.id),
                        order: getColumnOrder(helpersStudy.getScreenerAnsweredTableColumnId(screener.id)),
                        title: `Answered: ${screener.title}`,
                        sorter: true,
                        width: COL_WIDTH,
                        minWidth: COL_MIN_WIDTH,
                        sortOrder:
                            'sp' === sortObject.group &&
                            'screener_answered' === sortObject.column &&
                            screener.id === sortObject.id
                                ? sortObject.direction
                                : null,
                        onSort: () =>
                            onSort({
                                sort_group: 'sp',
                                sort_column: 'screener_answered',
                                sort_id: screener.id
                            }),
                        headerProps: {
                            className: 'ellipsis'
                        },
                        onCell: () => ({
                            className: 'ellipsis'
                        }),
                        render: studyPerson => renderScreenerAnswerCell(studyPerson, screener)
                    });
                }
            });
        }

        questions.forEach(question => {
            if (question.static) return;

            if (shouldShowColumn(helpersStudy.getScreenerQuestionTableColumnId(question.field_name))) {
                columns.push({
                    key: helpersStudy.getScreenerQuestionTableColumnId(question.field_name),
                    order: getColumnOrder(helpersStudy.getScreenerQuestionTableColumnId(question.field_name)),
                    title: helpers.removeHtml(question.label),
                    sorter: true,
                    width: COL_WIDTH,
                    minWidth: COL_MIN_WIDTH,
                    sortOrder:
                        'screener_q' === sortObject.group &&
                        question.screener_id === sortObject.column &&
                        question.field_name === sortObject.id
                            ? sortObject.direction
                            : null,
                    onSort: () =>
                        onSort({
                            sort_group: 'screener_q',
                            sort_column: question.screener_id,
                            sort_id: question.field_name
                        }),
                    headerProps: {
                        className: 'ellipsis'
                    },
                    onCell: () => ({
                        className: 'ellipsis'
                    }),
                    render: studyPerson => renderQuestionCell(studyPerson, question)
                });
            }
        });

        if (Array.isArray(study.screeners)) {
            study.screeners.forEach(screener => {
                if (shouldShowColumn(helpersStudy.getScreenerCustomVariabledTableColumnId(screener.id))) {
                    columns.push({
                        key: helpersStudy.getScreenerCustomVariabledTableColumnId(screener.id),
                        order: getColumnOrder(helpersStudy.getScreenerCustomVariabledTableColumnId(screener.id)),
                        title: `Custom Variable: ${screener.title}`,
                        sorter: false,
                        width: COL_WIDTH,
                        minWidth: COL_MIN_WIDTH,
                        headerProps: {
                            className: 'ellipsis'
                        },
                        onCell: () => ({
                            className: 'ellipsis'
                        }),
                        render: studyPerson => renderScreenerCustomVariableCell(studyPerson, screener)
                    });
                }
            });
        }

        // Panel property columns (opt-in via the Show Panel Properties modal).
        // Read straight from the persisted column list so we don't need any
        // side-channel — the containing page adds entries with `type: 'panel_property'`
        // whose `title` matches the panel property name used as the person
        // data key. ProtectedField handles PII masking.
        showHideColumns.forEach(col => {
            if (col.type !== PANEL_PROPERTY_TYPE) return;
            if (!col.checked) return;

            columns.push({
                key: col.id,
                order: getColumnOrder(col.id),
                title: col.title,
                sorter: false,
                width: COL_WIDTH,
                minWidth: COL_MIN_WIDTH,
                headerProps: {
                    className: 'ellipsis'
                },
                onCell: () => ({
                    className: 'ellipsis'
                }),
                render: studyPerson =>
                    studyPerson?.person ? (
                        <ProtectedField
                            hideable
                            person={studyPerson.person}
                            dataKey={col.title}
                            render={compactRender}
                            empty="-"
                        />
                    ) : (
                        '-'
                    )
            });
        });

        return columns.sort(helpersStudy.compareColumnOrder);
    }, [
        onSort,
        sort,
        setShowModalSelectCount,
        showCheckAllDropdown,
        checkAllOrVisibleStatus,
        setShowCheckAllDropdown,
        showHideColumns,
        study,
        sortObject,
        setSortObject,
        deselectAll,
        handleCheckAllDropdownChange,
        getScreenersList,
        adminFacing,
        showParticipantMoreDropdownSpId,
        setShowParticipantMoreDropdownSpId,
        p_checked,
        onCheckParticipant,
        onClickParticipant,
        getTooltip,
        getPersonIdentifier,
        questions,
        shouldShowColumn,
        getColumnOrder,
        onSearchKeywordChange,
        compactRender
    ]);
};

function StudyParticipantsList(props) {
    const [showCheckAllDropdown, setShowCheckAllDropdown] = useState(false);
    const [showParticipantMoreDropdownSpId, setShowParticipantMoreDropdownSpId] = useState(null);
    const [sortObject, setSortObject] = useState(props.sort);
    const [showModalSelectCount, setShowModalSelectCount] = useState(false);
    const [randomIsChecked, setRandomIsChecked] = useState(false);
    const [showModalQualtricsUiDatacenter, setShowModalQualtricsUiDatacenter] = useState(false);
    const columns = useStudyParticipantsTable({
        study: props.study,
        onSort: onClickSort,
        sort: props.sort,
        setShowModalSelectCount,
        setShowModalQualtricsUiDatacenter,
        showCheckAllDropdown,
        checkAllOrVisibleStatus: props.checkAllOrVisibleStatus,
        setShowCheckAllDropdown,
        showHideColumns: props.showHideColumns,
        sortObject,
        setSortObject,
        deselectAll: props.deselectAll,
        handleCheckAllDropdownChange: props.handleCheckAllDropdownChange,
        visibleStudyPeopleCount: props.study_people.data.length,
        totalStudyPeopleCount: props.study_people.total,
        adminFacing: props.adminFacing,
        onClickParticipant: props.onClickParticipant,
        showParticipantMoreDropdownSpId,
        setShowParticipantMoreDropdownSpId,
        auth: props.auth,
        p_checked: props.p_checked,
        onCheckParticipant: props.onCheckParticipant,
        onSearchKeywordChange: props.onSearchKeywordChange,
        onStudyPersonUpdate: props.onStudyPersonUpdate
    });

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [props.study_people]);

    function onClickSort({ sort_group, sort_column, sort_id, default_sort_direction }) {
        let newSortDirection;
        if (sort_group == sortObject.group && sort_column == sortObject.column && sort_id == sortObject.id) {
            // flip the sort direction
            newSortDirection = sortObject.direction == 'desc' ? 'asc' : 'desc';
        } else {
            newSortDirection = default_sort_direction ? default_sort_direction : 'desc';
        }

        const newSortObject = { group: sort_group, column: sort_column, id: sort_id, direction: newSortDirection };
        setSortObject(newSortObject);

        props.onChangeSort(newSortObject);
    }

    return (
        <Flex
            flexDirection="column"
            style={{ position: 'relative', width: '100%', height: `calc(100vh - 250px)`, minHeight: '200px' }}
        >
            {props.isLoading && (
                <div
                    style={{
                        pointerEvents: 'none',
                        position: 'absolute',
                        top: '44px',
                        left: '0',
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,.03)',
                        zIndex: 100
                    }}
                    data-testid="loading-overlay"
                >
                    <div style={{ height: '300px' }}>
                        <LoadingWrapper>
                            <LoadingIndicator />
                        </LoadingWrapper>
                    </div>
                </div>
            )}

            {props.study_people.data.length > 0 && (
                <TableV2
                    className="study-people-table overflow-x-auto pb-2"
                    resizable
                    size="sm"
                    isEditable
                    onRow={studyPerson => ({
                        onClick: event => helpers.clickTableRowAddStyle(event),
                        className: classNames(helpersStudy.isSpLocked(studyPerson) && 'row-being-viewed'),
                        onError: error => {
                            helpers.trackError(error);
                            return <Tr key={studyPerson.id}>Error in this row - it was reported to Private Panels.</Tr>;
                        }
                    })}
                    data={props.study_people.data}
                    columns={columns}
                />
            )}

            {props.study_people.data.length < props.study_people.total && (
                <Box sx={{ paddingTop: '8px' }}>
                    <Button type="button" variant="secondary-gray" onClick={props.loadMoreStudyPeople} mr={3}>
                        {props.study_people_loading_more ? 'Loading...' : 'Load more'}
                    </Button>

                    <span style={{ display: 'inline-block' }} className="va-top">
                        <Select
                            data-testid="study-people-pagination-limit"
                            value={props.pagination_limit}
                            onChange={e => props.onChangePaginationLimit(e.target.value)}
                        >
                            <option value="20">Show 20 per page</option>
                            <option value="50">Show 50 per page</option>
                            <option value="100">Show 100 per page</option>
                        </Select>
                    </span>
                </Box>
            )}

            <ReactModal
                isOpen={showModalSelectCount}
                shouldCloseOnOverlayClick
                onRequestClose={() => setShowModalSelectCount(false)}
                style={{ content: { width: '380px' } }}
            >
                <form
                    onSubmit={e => {
                        const count = document.getElementById('select_count').value;
                        if (count > 0 && helpers.isNumber(count)) {
                            props.handleCheckAllDropdownChange('custom_count', count, randomIsChecked);
                            setShowModalSelectCount(false);
                        }
                        e.preventDefault();
                    }}
                >
                    <Box m={4}>
                        <H1>Select Count</H1>
                        <Label>How many?</Label>
                        <Input type="number" id="select_count" autoFocus min="1" autoComplete="off" />

                        <Label mt={3}>
                            <Checkbox
                                name="daterange_enabled"
                                checked={randomIsChecked == 1}
                                onChange={e => {
                                    setRandomIsChecked(e.target.checked ? true : false);
                                }}
                            />
                            <span style={{ padding: '4px 0 0 0' }}>Randomize</span>
                        </Label>
                    </Box>

                    <ReactModalActions>
                        <div style={{ textAlign: 'right', width: '100%' }}>
                            <Button
                                type="button"
                                variant="transparent"
                                mr={3}
                                onClick={() => setShowModalSelectCount(false)}
                            >
                                Cancel
                            </Button>

                            <Button type="submit" mr={0} data-testid="modal-continue-button">
                                Continue
                            </Button>
                        </div>
                    </ReactModalActions>
                </form>
            </ReactModal>

            <NiceModal
                isOpen={showModalQualtricsUiDatacenter}
                shouldCloseOnOverlayClick
                onRequestClose={() => setShowModalQualtricsUiDatacenter(false)}
                style={{ content: { width: '480px' } }}
                title="Qualtrics UI Datacenter Required"
            >
                <Box mb={3}>
                    To view Qualtrics screener responses, please add your Qualtrics UI Datacenter ID in the integrations
                    settings.
                </Box>
                <Box mt={3} mb={3}>
                    <Link to="/settings/integrations" className="link">
                        Go to Integrations Settings
                    </Link>
                </Box>
            </NiceModal>
        </Flex>
    );
}

export default StudyParticipantsList;
